import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { Autoplay, Pagination, Swiper, SwiperSlide } from '@common/carousel/swiper';
import { useZigzag } from '@common/device-manager';
import {
  BannerGroup,
  HEADER_HEIGHT,
  NAVIGATION_HEIGHT,
  ShopUxBannerLinkType,
  useStoreHomeUBL,
} from '@domains/storehome-common';

import { SelectedBannerCounter } from '../components/SelectedBannerCounter';
import { SelectedBannerItem } from '../components/SelectedBannerItem';
import { DEFAULT_DELAY } from '../constants/swiper';
import { SWIPER_STYLE } from '../styles/swiper';
import { convertBannerLink } from '../utils/convertBannerLink';

interface Props {
  banner_group: BannerGroup | null;
  is_preview?: boolean;
  shop_id: string;
}

export const SelectedMainBannerContainer: FC<React.PropsWithChildren<Props>> = ({
  banner_group,
  is_preview,
  shop_id,
}) => {
  const [active_index, setActiveIndex] = useState(0);

  const is_app = useZigzag(false);
  const { bannerClickUBL, bannerImpressionUBL } = useStoreHomeUBL(shop_id);
  const router = useRouter();
  const { query } = router;

  const handleBannerItemClick =
    (index?: number) =>
    ({ link, link_type, id }: { link: string | undefined; link_type: ShopUxBannerLinkType | null; id: string }) => {
      const { link: converted_link, is_legacy } = convertBannerLink({
        link,
        link_type,
        shop_main_domain: query.main_domain,
      });
      bannerClickUBL({
        id,
        type: !link_type ? 'STOREPICK' : (link_type as string),
        url: converted_link ?? '',
        index: index || 0,
      });
      if (converted_link) {
        is_legacy ? window.open(decodeURIComponent(converted_link), '_blank') : router.push(converted_link);
      }
    };

  const handleBannerImpression = ({
    link_type,
    id,
    index,
  }: {
    link_type: ShopUxBannerLinkType | null;
    id: string;
    index: number;
  }) => {
    bannerImpressionUBL({
      id,
      type: !link_type ? 'STOREPICK' : (link_type as string),
      index: index || 0,
    });
  };

  if (!banner_group) {
    return <Spacer />;
  }

  const { auto_rolling_interval, is_auto_rolling, banner_list } = banner_group;
  const is_single_banner = banner_list?.length === 1;

  return (
    <BannerSwiper
      is_app={Boolean(is_app)}
      is_preview={Boolean(is_preview)}
      className={is_single_banner ? 'single-banner' : ''}
    >
      {is_single_banner ? (
        <SelectedBannerItem banner_item={banner_list[0]} onClick={handleBannerItemClick(0)} />
      ) : (
        <Swiper
          modules={[Autoplay, Pagination]}
          loop={is_auto_rolling}
          autoplay={{
            delay: auto_rolling_interval ? auto_rolling_interval * 1000 : DEFAULT_DELAY,
            disableOnInteraction: false,
          }}
          onSlideChange={(e) => {
            setActiveIndex(e.realIndex);
            const banner_item = banner_list?.[e.realIndex];
            handleBannerImpression({
              index: e.realIndex,
              id: banner_item?.id ?? '',
              link_type: banner_item?.link_type ?? null,
            });
          }}
        >
          {banner_list &&
            banner_list.map((item, index) => (
              <SwiperSlide key={`main-${item.name}-${index}`}>
                <SelectedBannerItem banner_item={item} onClick={handleBannerItemClick(index)} />
              </SwiperSlide>
            ))}
          <SelectedBannerCounter active_index={active_index} total_count={banner_list?.length ?? 0} />
        </Swiper>
      )}
    </BannerSwiper>
  );
};

const BannerSwiper = styled.div<{ is_app: boolean; is_preview: boolean }>`
  ${SWIPER_STYLE}
  max-width: 600px;
  width: 100%;
  height: auto;
  max-height: 720px;
  top: 0;
  padding: 0;

  .swiper-slide {
    max-height: 720px;
  }

  .swiper-pagination {
    &.swiper-pagination-bullets {
      bottom: 25px;

      .swiper-pagination-bullet {
        background-color: #ffffff;
        opacity: 0.5;
        width: 6px;
        height: 6px;
        margin: 0 2px;
      }

      .swiper-pagination-bullet-active {
        opacity: 1;
      }
    }
  }
`;

const Spacer = styled.div`
  padding: ${NAVIGATION_HEIGHT + HEADER_HEIGHT + 32}px 0 0;
`;
